import { FunctionField, ListProps, TextField } from 'react-admin';

import { Grid, GridList, Forbidden, DateField, BooleanField } from '@components';
import { useRbacList } from '@hooks';
import { CirculoReport } from '@types';

const renderReportField = (key: keyof CirculoReport) => (record: any) =>
  <span>{record.circuloReport?.[key] ?? 'N/A'}</span>;

const formatDomicilio = (domicilio: CirculoReport['domicilioPm']) => {
  if (!domicilio) return 'N/A';

  return [domicilio.calleNumero, domicilio.colonia, domicilio.ciudad, domicilio.estado]
    .filter(Boolean).join(', ') || 'N/A';
};

export const SyntageAutorizationsList = (props: ListProps) => {
  const rbacList = useRbacList();

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <GridList
      {...props}
      filters={[]}
      sort={{ field: 'id', order: 'DESC' }}
      exporter={false}
    >
      <Grid>
        <FunctionField
          source="record.user"
          label="RFC"
          render={(record: any) => {
            const rfc = record.user.isCompany ? record.user.rfcCompany : record.user.rfc;
            return (
              <span>{rfc}</span>
            );
          }}
        />
        <FunctionField
          source="record.user"
          label="Client Name / Razon Social"
          render={(record: any) => {
            const rfc = record.user.isCompany ? record.user.companyName : record.user.fullName;
            return (
              <span>{rfc}</span>
            );
          }}
        />
        <FunctionField
          source="record.user"
          label="Client Type"
          render={(record: any) => {
            const rfc = record.user.isCompany ? 'PM' : 'PF';
            return (
              <span>{rfc}</span>
            );
          }}
        />
        <BooleanField source="isActive" label="Auth. Syntage Vigente" />

        <FunctionField
          source="record.status"
          label="Syntage Status"
          render={(record: any) => {
            const displayStatus = record.status === 'invalid' ? 'CIEC invalid' : record.status;

            return (
              <span style={{ textTransform: 'capitalize' }}>{displayStatus}</span>
            );
          }}
        />

        <FunctionField
          source="record.circuloProfile"
          label="Circulo Status"
          render={(record: any) => {
            const profile = record.circuloProfile;
            if (!profile) return <span>N/A</span>;

            const response = profile.isCompany ? profile.pmResponse : profile.pfResponse;
            const error = profile.isCompany ? profile.pmError : profile.pfError;
            const displayStatus = response ? 'Valid' : error ? 'Error' : 'Pending';

            return (
              <span style={{ textTransform: 'capitalize' }}>{displayStatus}</span>
            );
          }}
        />

        <FunctionField
          source="record.circuloReport.folioCdc"
          label="FOLIO CDC"
          render={renderReportField('folioCdc')}
        />
        <FunctionField
          source="record.circuloReport.fechaAprobacionDeConsulta"
          label="FECHA APROBACION DE CONSULTA"
          render={renderReportField('fechaAprobacionDeConsulta')}
        />
        <FunctionField
          source="record.circuloReport.horaAprobacionDeConsulta"
          label="HORA APROBACION DE CONSULTA"
          render={renderReportField('horaAprobacionDeConsulta')}
        />
        <FunctionField
          source="record.circuloReport.nombreCliente"
          label="NOMBRE CLIENTE"
          render={renderReportField('nombreCliente')}
        />
        <FunctionField
          source="record.circuloReport.rfc"
          label="RFC"
          render={renderReportField('rfc')}
        />
        <FunctionField
          source="record.circuloReport.calleNumero"
          label="CALLE NUMERO"
          render={renderReportField('calleNumero')}
        />
        <FunctionField
          source="record.circuloReport.colonia"
          label="COLONIA"
          render={renderReportField('colonia')}
        />
        <FunctionField
          source="record.circuloReport.ciudad"
          label="CIUDAD"
          render={renderReportField('ciudad')}
        />
        <FunctionField
          source="record.circuloReport.estado"
          label="ESTADO"
          render={renderReportField('estado')}
        />
        <FunctionField
          source="record.circuloReport.tipoConsulta"
          label="TIPO CONSULTA"
          render={renderReportField('tipoConsulta')}
        />
        <FunctionField
          source="record.circuloReport.usuario"
          label="USUARIO"
          render={renderReportField('usuario')}
        />
        <FunctionField
          source="record.circuloReport.fechaConsulta"
          label="FECHA CONSULTA"
          render={renderReportField('fechaConsulta')}
        />
        <FunctionField
          source="record.circuloReport.horaConsulta"
          label="HORA CONSULTA"
          render={renderReportField('horaConsulta')}
        />
        <FunctionField
          source="record.circuloReport.ingresoNipCiec"
          label="INGRESO NIP CIEC"
          render={renderReportField('ingresoNipCiec')}
        />
        <FunctionField
          source="record.circuloReport.respuestaLeyendaDeAutorizacion"
          label="RESPUESTA LEYENDA DE AUTORIZACION"
          render={renderReportField('respuestaLeyendaDeAutorizacion')}
        />
        <FunctionField
          source="record.circuloReport.aceptacionTerminosYCondiciones"
          label="ACEPTACION TERMINOS Y CONDICIONES"
          render={renderReportField('aceptacionTerminosYCondiciones')}
        />

        {/* Only applicable when the client is a company (PM) */}
        <FunctionField
          source="record.circuloReport.razonSocial"
          label="RAZON SOCIAL"
          render={renderReportField('razonSocial')}
        />
        <FunctionField
          source="record.circuloReport.rfcPm"
          label="RFC PM"
          render={renderReportField('rfcPm')}
        />
        <FunctionField
          source="record.circuloReport.domicilioPm"
          label="DOMICILIO PM"
          render={(record: any) =>
            <span>{formatDomicilio(record.circuloReport?.domicilioPm)}</span>}
        />
        <FunctionField
          source="record.circuloReport.nombreRepresentanteLegal"
          label="NOMBRE REPRESENTANTE LEGAL"
          render={renderReportField('nombreRepresentanteLegal')}
        />
        <FunctionField
          source="record.circuloReport.rfcRl"
          label="RFC RL"
          render={renderReportField('rfcRl')}
        />
        <FunctionField
          source="record.circuloReport.domicilioRl"
          label="DOMICILIO RL"
          render={(record: any) =>
            <span>{formatDomicilio(record.circuloReport?.domicilioRl)}</span>}
        />

        <DateField source="createdAt" label="Auth. Syntage Date" showTime />
      </Grid>
    </GridList>
  );
};
